import React, { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import { useTranslation } from "react-i18next";
import { CATEGORY_KEYS } from "./i18n";
import {
  Search,
  Plus,
  MessageSquare,
  User,
  LogOut,
  Edit,
  Trash2,
  Send,
  Loader,
  Menu,
  X,
  Globe,
} from "lucide-react";

// Load environment variables
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;

// Validate environment variables
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error(
    "Missing Supabase environment variables. Please check your .env file."
  );
}

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Helper function to convert URLs in text to clickable links
const linkifyText = (text) => {
  if (!text) return null;

  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // Split text by URLs
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    // Check if this part is a URL
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all"
        >
          {part}
        </a>
      );
    }
    // Return text as is, preserving line breaks
    return part.split("\n").map((line, i, arr) => (
      <React.Fragment key={`${index}-${i}`}>
        {line}
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  });
};

// Helper function to get category key from translated category name
const getCategoryKeyFromName = (categoryName, t) => {
  // Find the category key by comparing the translated name
  for (const key of CATEGORY_KEYS) {
    if (t(`category.${key}`) === categoryName) {
      return key;
    }
  }
  // If not found, try to find by English name match
  const categoryMap = {
    Design: "design",
    Writing: "writing",
    Programming: "programming",
    Marketing: "marketing",
    "Video & Animation": "videoAnimation",
    "Music & Audio": "musicAudio",
    Business: "business",
    Data: "data",
    Translation: "translation",
  };
  return categoryMap[categoryName] || "all";
};

// Navigation Component
function Navigation({ user, onLogout, onNavigate, currentView }) {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "zh" : "en";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <h1
            className="text-2xl font-bold text-blue-600 cursor-pointer"
            onClick={() => onNavigate("home")}
          >
            {t("nav.title")}
          </h1>

          {/* Desktop Menu */}
          <div className="hidden md:flex gap-4 items-center">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
              title={
                i18n.language === "en" ? "切換至中文" : "Switch to English"
              }
            >
              <Globe size={20} />
              <span className="text-sm font-medium">
                {i18n.language === "en" ? "中文" : "EN"}
              </span>
            </button>
            {user ? (
              <>
                <button
                  onClick={() => onNavigate("createGig")}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  <Plus size={20} />
                  {t("nav.postGig")}
                </button>
                <button
                  onClick={() => onNavigate("myGigs")}
                  className="text-gray-600 hover:text-gray-800 transition"
                >
                  {t("nav.myGigs")}
                </button>
                <button
                  onClick={() => onNavigate("profile")}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition"
                >
                  <User size={20} />
                  {t("nav.profile")}
                </button>
                <button
                  onClick={onLogout}
                  className="flex items-center gap-2 text-red-600 hover:text-red-800 transition"
                >
                  <LogOut size={20} />
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => onNavigate("login")}
                  className="text-gray-600 hover:text-gray-800 transition"
                >
                  {t("nav.login")}
                </button>
                <button
                  onClick={() => onNavigate("signup")}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                  {t("nav.signUp")}
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-2">
            {/* Language Switcher in Mobile */}
            <button
              onClick={() => {
                toggleLanguage();
                setMobileMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50 rounded flex items-center gap-2"
            >
              <Globe size={20} />
              {i18n.language === "en" ? "切換至中文" : "Switch to English"}
            </button>
            {user ? (
              <>
                <button
                  onClick={() => {
                    onNavigate("createGig");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50 rounded"
                >
                  {t("nav.postGig")}
                </button>
                <button
                  onClick={() => {
                    onNavigate("myGigs");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50 rounded"
                >
                  {t("nav.myGigs")}
                </button>
                <button
                  onClick={() => {
                    onNavigate("profile");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50 rounded"
                >
                  {t("nav.profile")}
                </button>
                <button
                  onClick={() => {
                    onLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-red-600 hover:bg-gray-50 rounded"
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    onNavigate("login");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50 rounded"
                >
                  {t("nav.login")}
                </button>
                <button
                  onClick={() => {
                    onNavigate("signup");
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded"
                >
                  {t("nav.signUp")}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

// Auth Component
function AuthView({ onSuccess, onBackToHome, initialMode = "login" }) {
  const { t } = useTranslation();
  const [authMode, setAuthMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (authMode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName },
            emailRedirectTo: SITE_URL,
          },
        });
        if (error) {
          // Check for specific error cases
          if (
            error.message.includes("already registered") ||
            error.message.includes("User already registered")
          ) {
            alert(t("auth.emailExists"));
          } else {
            alert(error.message);
          }
          throw error;
        }
        setSignupSuccess(true);
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) {
          // Provide user-friendly error messages
          if (error.message.includes("Invalid login credentials")) {
            alert(t("auth.invalidCredentials"));
          } else {
            alert(error.message);
          }
          throw error;
        }
        onSuccess();
      }
    } catch (error) {
      // Error already handled above
    } finally {
      setLoading(false);
    }
  };

  // Show success message after signup
  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold mb-4 text-gray-800">
              {t("confirm.checkEmail")}
            </h2>
            <p className="text-gray-600 mb-2">{t("confirm.sentTo")}</p>
            <p className="text-blue-600 font-semibold mb-4">{email}</p>
            <p className="text-gray-600">{t("confirm.clickLink")}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700 mb-2">
              <strong>{t("confirm.didntReceive")}</strong>{" "}
              {t("confirm.checkSpam")}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              {t("confirm.alreadyRegistered")}
            </p>
          </div>
          <div className="flex gap-4 justify-center">
            <button
              onClick={onBackToHome}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
            >
              {t("auth.home")}
            </button>
            <button
              onClick={() => {
                setSignupSuccess(false);
                setAuthMode("login");
              }}
              className="px-6 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition font-semibold"
            >
              {t("nav.login")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
          {t(authMode === "login" ? "auth.welcomeBack" : "auth.createAccount")}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {authMode === "signup" && (
            <input
              type="text"
              placeholder={t("auth.fullName")}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              required
            />
          )}
          <input
            type="email"
            placeholder={t("auth.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
            required
          />
          <input
            type="password"
            placeholder={t("auth.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-semibold"
          >
            {loading
              ? t("auth.processing")
              : t(authMode === "login" ? "auth.login" : "auth.signUp")}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-gray-600">
          {t(authMode === "login" ? "auth.noAccount" : "auth.haveAccount")}
          <button
            onClick={() =>
              setAuthMode(authMode === "login" ? "signup" : "login")
            }
            className="text-blue-600 hover:underline font-semibold"
          >
            {t(authMode === "login" ? "auth.signUp" : "auth.login")}
          </button>
        </p>
        <p className="mt-4 text-center text-sm text-gray-600">
          {t("auth.orGoBack")}
          <button
            onClick={onBackToHome}
            className="text-blue-600 hover:underline font-semibold"
          >
            {t("auth.home")}
          </button>
        </p>
        {authMode === "login" && (
          <p className="mt-4 text-center text-xs text-gray-500">
            {t("auth.havingTrouble")}{" "}
            <a
              href="mailto:takaocybercorner@gmail.com"
              className="text-blue-600 hover:underline"
            >
              takaocybercorner@gmail.com
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

// Gig Card Component
function GigCard({
  gig,
  onClick,
  showActions,
  onEdit,
  onDelete,
  onReactivate,
}) {
  const { t } = useTranslation();

  // Translate category name
  const categoryKey = getCategoryKeyFromName(gig.category, t);
  const translatedCategory = t(`category.${categoryKey}`);

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group">
      <div onClick={onClick}>
        {gig.image_url ? (
          <img
            src={gig.image_url}
            alt={gig.title}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-48 bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
            <span className="text-white text-4xl font-bold">
              {gig.title[0]}
            </span>
          </div>
        )}
        <div className="p-4">
          {showActions && (
            <div className="flex items-center justify-between mb-2">
              <span
                className={`px-2 py-1 text-xs rounded font-semibold ${
                  gig.status === "active"
                    ? "bg-green-100 text-green-800"
                    : gig.status === "pending"
                    ? "bg-yellow-100 text-yellow-800"
                    : gig.status === "expired"
                    ? "bg-gray-100 text-gray-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {t(`gigs.status.${gig.status}`)}
              </span>
              <span className="text-sm text-gray-500 font-medium">
                {translatedCategory}
              </span>
            </div>
          )}
          {!showActions && (
            <span className="text-sm text-blue-600 font-medium">
              {translatedCategory}
            </span>
          )}
          <h3 className="font-bold text-lg mb-2 line-clamp-1 group-hover:text-blue-600 transition">
            {gig.title}
          </h3>
          <p className="text-gray-600 text-sm mb-3 line-clamp-2">
            {gig.description}
          </p>

          {gig.profiles && !showActions && (
            <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                {gig.profiles.full_name?.[0] || "U"}
              </div>
              <span>{gig.profiles.full_name || "Anonymous"}</span>
            </div>
          )}

          {!showActions && (
            <div className="mt-3">
              <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition font-medium text-sm">
                {t("gigCard.viewDetails")}
              </button>
            </div>
          )}

          {showActions && gig.status === "rejected" && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <strong>Rejected:</strong> {gig.rejection_reason}
            </div>
          )}
        </div>
      </div>

      {showActions && (
        <div className="p-4 pt-0 flex gap-2">
          {gig.status === "expired" ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReactivate(gig.id);
              }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Plus size={16} />
              {t("gigCard.reactivate")}
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(gig);
              }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <Edit size={16} />
              {t("gigCard.edit")}
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(gig.id);
            }}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// Home View Component
function HomeView({ user, gigs, loading, onGigClick, onNavigate }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredGigs = gigs.filter((gig) => {
    const matchesSearch =
      gig.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      gig.description.toLowerCase().includes(searchQuery.toLowerCase());

    // Match category by checking both the current language and original stored value
    const matchesCategory =
      selectedCategory === "all" ||
      gig.category === t(`category.${selectedCategory}`) ||
      getCategoryKeyFromName(gig.category, t) === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-4">
            {t("hero.title")}
          </h1>
          <p className="text-xl text-center mb-8 text-blue-100">
            {t("hero.subtitle")}
          </p>

          {/* Prominent Search Bar */}
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <Search
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={24}
              />
              <input
                type="text"
                placeholder={t("hero.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-14 pr-4 py-4 rounded-xl text-gray-800 text-lg shadow-2xl focus:ring-4 focus:ring-blue-300 outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white shadow-sm sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CATEGORY_KEYS.map((catKey) => (
              <button
                key={catKey}
                onClick={() => setSelectedCategory(catKey)}
                className={`px-4 py-2 rounded-full whitespace-nowrap transition ${
                  selectedCategory === catKey
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {t(`category.${catKey}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gigs Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {t(`category.${selectedCategory}`)}
          </h2>
          <p className="text-gray-600">
            {filteredGigs.length} {t("gigs.found")}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader className="animate-spin text-blue-600" size={48} />
          </div>
        ) : filteredGigs.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg mb-4">{t("gigs.noGigs")}</p>
            {user && (
              <button
                onClick={() => onNavigate("createGig")}
                className="text-blue-600 hover:underline"
              >
                {t("gigs.beFirst")}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredGigs.map((gig) => (
              <GigCard key={gig.id} gig={gig} onClick={() => onGigClick(gig)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Gig Form Component
function GigFormView({ user, initialGig, onSuccess, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(
    initialGig || {
      title: "",
      description: "",
      category: "Design",
      price: "",
      imageUrl: "",
      status: "pending",
    }
  );
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      alert("Please login to create a gig");
      return;
    }

    setLoading(true);
    try {
      if (form.id) {
        // Update existing gig
        const updateData = {
          title: form.title,
          description: form.description,
          category: form.category,
          price: 0,
          image_url: form.imageUrl,
        };

        // Only update status if it's being changed to inactive
        // If changing to active, it needs moderation
        if (form.status === "inactive") {
          updateData.status = "inactive";
        } else if (
          initialGig.status === "inactive" &&
          form.status === "active"
        ) {
          updateData.status = "pending"; // Reactivating requires review
        }

        // If reactivating from inactive, set to pending for review
        if (initialGig.status === "inactive" && form.status === "active") {
          updateData.status = "pending";
        }

        const { error } = await supabase
          .from("gigs")
          .update(updateData)
          .eq("id", form.id);

        if (error) throw error;

        alert(
          form.status === "inactive"
            ? t("gigForm.gigDeactivated")
            : t("gigForm.gigUpdated")
        );
      } else {
        // Create new gig - automatically set to active (published immediately)
        const { data, error } = await supabase
          .from("gigs")
          .insert([
            {
              user_id: user.id,
              title: form.title,
              description: form.description,
              category: form.category,
              price: 0,
              image_url: form.imageUrl,
              status: "active", // All new gigs are active immediately
            },
          ])
          .select()
          .single();

        if (error) throw error;

        alert(t("gigForm.gigCreatedSuccess"));
      }
      onSuccess();
    } catch (error) {
      alert(error.message || "Failed to save gig");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold mb-6">
            {t(form.id ? "gigForm.edit" : "gigForm.create")}
          </h2>

          {!form.id && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                ℹ️ {t("gigForm.expiryNotice")}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                {t("gigForm.title")}
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                placeholder={t("gigForm.titlePlaceholder")}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                {t("gigForm.description")}
              </label>
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition h-32"
                placeholder={t("gigForm.descriptionPlaceholder")}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                {t("gigForm.category")}
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              >
                {CATEGORY_KEYS.filter((key) => key !== "all").map((key) => (
                  <option key={key} value={t(`category.${key}`)}>
                    {t(`category.${key}`)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                {t("gigForm.imageUrl")}
              </label>
              <input
                type="url"
                value={form.imageUrl}
                onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                placeholder={t("gigForm.imageUrlPlaceholder")}
              />
            </div>

            {form.id &&
              (initialGig.status === "active" ||
                initialGig.status === "inactive") && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">
                    {t("gigForm.status")}
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="status"
                        value="active"
                        checked={form.status === "active"}
                        onChange={(e) =>
                          setForm({ ...form, status: e.target.value })
                        }
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-gray-700">
                        {t("gigForm.statusActive")}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="status"
                        value="inactive"
                        checked={form.status === "inactive"}
                        onChange={(e) =>
                          setForm({ ...form, status: e.target.value })
                        }
                        className="w-4 h-4 text-gray-600"
                      />
                      <span className="text-gray-700">
                        {t("gigForm.statusInactive")}
                      </span>
                    </label>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {t("gigForm.statusHelp")}
                  </p>
                </div>
              )}

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-semibold"
              >
                {loading
                  ? t("auth.processing")
                  : t(
                      form.id ? "gigForm.updateButton" : "gigForm.createButton"
                    )}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold"
              >
                {t("gigForm.cancel")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// My Gigs View
function MyGigsView({ user, onNavigate }) {
  const { t } = useTranslation();
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMyGigs = async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("gigs")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (!error) setGigs(data || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchMyGigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleDelete = async (gigId) => {
    if (!window.confirm(t("myGigs.deleteConfirm"))) return;

    const { error } = await supabase.from("gigs").delete().eq("id", gigId);

    if (!error) fetchMyGigs();
  };

  const handleEdit = (gig) => {
    onNavigate("editGig", gig);
  };

  const handleReactivate = async (gigId) => {
    if (!window.confirm(t("myGigs.reactivateConfirm"))) return;

    setLoading(true);
    const { error } = await supabase
      .from("gigs")
      .update({
        status: "pending",
        last_activated_at: new Date().toISOString(),
      })
      .eq("id", gigId);

    if (!error) {
      alert(t("myGigs.reactivateSuccess"));
      fetchMyGigs();
    } else {
      alert(t("myGigs.reactivateError"));
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold">{t("myGigs.title")}</h2>
          <button
            onClick={() => onNavigate("createGig")}
            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={20} />
            {t("myGigs.newGig")}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader className="animate-spin text-blue-600" size={48} />
          </div>
        ) : gigs.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow-sm">
            <p className="text-gray-500 text-lg mb-4">{t("myGigs.noGigs")}</p>
            <button
              onClick={() => onNavigate("createGig")}
              className="text-blue-600 hover:underline font-semibold"
            >
              {t("myGigs.createFirst")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gigs.map((gig) => (
              <GigCard
                key={gig.id}
                gig={gig}
                showActions
                onClick={() => {}}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onReactivate={handleReactivate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Gig Detail View
function GigDetailView({ gig, user, onBack, onNavigate }) {
  const { t } = useTranslation();
  const [freelancerEmail, setFreelancerEmail] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(true);

  useEffect(() => {
    fetchFreelancerEmail();
  }, [gig]);

  const fetchFreelancerEmail = async () => {
    if (!gig.user_id) return;

    // Fetch email from profiles table (email should be stored there)
    const { data, error } = await supabase
      .from("profiles")
      .select("email")
      .eq("id", gig.user_id)
      .single();

    if (!error && data && data.email) {
      setFreelancerEmail(data.email);
    }
    setLoadingEmail(false);
  };

  // Translate category name
  const categoryKey = getCategoryKeyFromName(gig.category, t);
  const translatedCategory = t(`category.${categoryKey}`);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <button
          onClick={onBack}
          className="mb-6 text-gray-600 hover:text-gray-800 flex items-center gap-2"
        >
          {t("gigDetail.back")}
        </button>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {gig.image_url && (
            <img
              src={gig.image_url}
              alt={gig.title}
              className="w-full h-80 object-cover"
            />
          )}

          <div className="p-8">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-3">
                {translatedCategory}
              </span>
              <h1 className="text-4xl font-bold text-gray-800">{gig.title}</h1>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-bold mb-3 text-gray-800">
                {t("gigDetail.aboutGig")}
              </h2>
              <div className="text-gray-700 leading-relaxed">
                {linkifyText(gig.description)}
              </div>
            </div>

            <div className="border-t pt-8">
              <h3 className="text-lg font-bold mb-4 text-gray-800">
                {t("gigDetail.aboutFreelancer")}
              </h3>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {gig.profiles?.full_name?.[0] || "U"}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-lg">
                    {gig.profiles?.full_name || "Anonymous"}
                  </p>
                  {gig.profiles?.bio && (
                    <p className="text-gray-600 mt-1">{gig.profiles.bio}</p>
                  )}
                </div>
              </div>

              {user ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h4 className="font-bold text-gray-800 mb-3 text-lg">
                    {t("gigDetail.contactInfo")}
                  </h4>
                  {loadingEmail ? (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Loader className="animate-spin" size={16} />
                      <span>Loading contact information...</span>
                    </div>
                  ) : freelancerEmail ? (
                    <div>
                      <p className="text-gray-600 mb-2 text-sm">
                        {t("gigDetail.emailLabel")}
                      </p>
                      <a
                        href={`mailto:${freelancerEmail}`}
                        className="text-blue-600 hover:text-blue-800 font-semibold text-lg break-all"
                      >
                        {freelancerEmail}
                      </a>
                    </div>
                  ) : (
                    <p className="text-gray-600">
                      Contact information not available
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                  <p className="text-gray-700 mb-3">
                    {t("gigDetail.loginToContact")}
                  </p>
                  <button
                    onClick={() => onNavigate("login")}
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    {t("nav.login")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Footer Component
function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-gray-800 text-white py-8 mt-auto">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center">
          <p className="text-gray-300 mb-2">{t("footer.contactUs")}</p>
          <a
            href="mailto:takaocybercorner@gmail.com"
            className="text-blue-400 hover:text-blue-300 transition font-medium"
          >
            takaocybercorner@gmail.com
          </a>
          <p className="text-gray-400 text-sm mt-4">
            © {new Date().getFullYear()} {t("nav.title")}. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}

// Profile View (Messages functionality removed)
function ProfileView({ user }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ full_name: "", bio: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    if (!error && data) {
      setProfile(data);
      setForm({ full_name: data.full_name || "", bio: data.bio || "" });
    }
    setLoading(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: form.full_name,
        bio: form.bio,
      })
      .eq("id", user.id);

    if (!error) {
      setEditing(false);
      fetchProfile();
      alert("Profile updated successfully!");
    } else {
      alert("Failed to update profile");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow-md p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-bold">{t("profile.title")}</h2>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
              >
                <Edit size={20} />
                {t("profile.edit")}
              </button>
            )}
          </div>

          {editing ? (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  {t("profile.fullName")}
                </label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) =>
                    setForm({ ...form, full_name: e.target.value })
                  }
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2">
                  {t("profile.bio")}
                </label>
                <textarea
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none h-32"
                  placeholder={t("profile.bioPlaceholder")}
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? t("profile.saving") : t("profile.saveChanges")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setForm({
                      full_name: profile?.full_name || "",
                      bio: profile?.bio || "",
                    });
                  }}
                  className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                >
                  {t("profile.cancel")}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                  {profile?.full_name?.[0] || "U"}
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {profile?.full_name || "Anonymous"}
                  </p>
                  <p className="text-gray-500">{user.email}</p>
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">
                  {t("profile.bio")}
                </p>
                <p className="text-gray-800">
                  {profile?.bio || t("profile.noBio")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main App Component
export default function FreelanceMarketplace() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState("home");
  const [gigs, setGigs] = useState([]);
  const [selectedGig, setSelectedGig] = useState(null);
  const [editingGig, setEditingGig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkUser();
    fetchGigs();
  }, []);

  const checkUser = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    setUser(session?.user || null);
  };

  const fetchGigs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("gigs")
      .select(
        `
        *,
        profiles (full_name, avatar_url, bio)
      `
      )
      .eq("status", "active")
      .not("status", "in", "(expired,inactive)")
      .order("created_at", { ascending: false });

    if (!error) setGigs(data || []);
    setLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setView("home");
  };

  const handleNavigate = (newView, data = null) => {
    setView(newView);
    if (newView === "editGig") {
      setEditingGig(data);
    } else {
      setEditingGig(null);
    }
    if (newView === "home") {
      setSelectedGig(null);
      fetchGigs();
    }
  };

  const handleGigClick = (gig) => {
    setSelectedGig(gig);
  };

  // Render views
  if (view === "login" || view === "signup") {
    return (
      <AuthView
        initialMode={view === "signup" ? "signup" : "login"}
        onSuccess={() => {
          checkUser();
          setView("home");
        }}
        onBackToHome={() => setView("home")}
      />
    );
  }

  if (view === "createGig" || view === "editGig") {
    return (
      <div className="flex flex-col min-h-screen">
        <Navigation
          user={user}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          currentView={view}
        />
        <div className="flex-1">
          <GigFormView
            user={user}
            initialGig={editingGig}
            onSuccess={() => handleNavigate("myGigs")}
            onCancel={() => handleNavigate(editingGig ? "myGigs" : "home")}
          />
        </div>
        <Footer />
      </div>
    );
  }

  if (view === "myGigs") {
    return (
      <div className="flex flex-col min-h-screen">
        <Navigation
          user={user}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          currentView={view}
        />
        <div className="flex-1">
          <MyGigsView user={user} onNavigate={handleNavigate} />
        </div>
        <Footer />
      </div>
    );
  }

  if (view === "profile") {
    return (
      <div className="flex flex-col min-h-screen">
        <Navigation
          user={user}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          currentView={view}
        />
        <div className="flex-1">
          <ProfileView user={user} />
        </div>
        <Footer />
      </div>
    );
  }

  if (selectedGig) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navigation
          user={user}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
          currentView={view}
        />
        <div className="flex-1">
          <GigDetailView
            gig={selectedGig}
            user={user}
            onBack={() => setSelectedGig(null)}
            onNavigate={handleNavigate}
          />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navigation
        user={user}
        onLogout={handleLogout}
        onNavigate={handleNavigate}
        currentView={view}
      />
      <div className="flex-1">
        <HomeView
          user={user}
          gigs={gigs}
          loading={loading}
          onGigClick={handleGigClick}
          onNavigate={handleNavigate}
        />
      </div>
      <Footer />
    </div>
  );
}
